import React, { useState, useRef, useEffect } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import { Button } from "@/components/ui/button";
import { Link, Unlink, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

interface SynchronizedPdfViewerProps {
  pdfUrl: string;
  children: React.ReactNode;
  className?: string;
}

export const SynchronizedPdfViewer: React.FC<SynchronizedPdfViewerProps> = ({
  pdfUrl,
  children,
  className = "",
}) => {
  const [isLinkedScrolling, setIsLinkedScrolling] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);

  // Initialize zoom plugin
  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  const handleFormScroll = () => {
    if (!isLinkedScrolling || isScrolling) return;

    setIsScrolling(true);

    const formContainer = formContainerRef.current;
    const pdfContainer = pdfContainerRef.current;

    if (formContainer && pdfContainer) {
      // Calculate scroll percentage from form
      const formScrollTop = formContainer.scrollTop;
      const formScrollHeight =
        formContainer.scrollHeight - formContainer.clientHeight;
      const scrollPercentage =
        formScrollHeight > 0 ? formScrollTop / formScrollHeight : 0;

      // Find the actual PDF viewer scrollable element
      const pdfViewerElement = pdfContainer.querySelector(
        ".rpv-core__viewer-body"
      );
      if (pdfViewerElement) {
        const pdfScrollHeight =
          pdfViewerElement.scrollHeight - pdfViewerElement.clientHeight;
        const targetScrollTop = scrollPercentage * pdfScrollHeight;
        pdfViewerElement.scrollTop = targetScrollTop;
      }
    }

    // Use requestAnimationFrame to ensure smooth scrolling
    requestAnimationFrame(() => {
      setIsScrolling(false);
    });
  };
  console.log("djandjsn", formContainerRef.current);
  useEffect(() => {
    const formContainer = formContainerRef.current;

    formContainer?.addEventListener("scroll", handleFormScroll, {
      passive: true,
    });
    return () => {
      formContainer?.removeEventListener("scroll", handleFormScroll);
    };
  }, []);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Control Bar */}
      <div className="flex justify-between items-center">
        {/* PDF Controls */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border rounded-md p-1">
            <ZoomOutButton>
              {(props) => (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={props.onClick}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
              )}
            </ZoomOutButton>
            <Button
              onClick={() => {
                const element = document.querySelector(
                  ".rpv-core__viewer-body"
                );
                const scrollHeight =
                  element?.scrollHeight - element?.clientHeight;
                const scrollTop = element?.scrollTop;
                const scrollPercentage = scrollTop / scrollHeight;
                console.log(scrollPercentage);
                console.log(element?.scrollHeight);
                console.log(element?.clientHeight);
                console.log(element?.scrollTop);
              }}
            >
              Test
            </Button>
            <ZoomPopover />
            <ZoomInButton>
              {(props) => (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={props.onClick}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              )}
            </ZoomInButton>
          </div>
        </div>

        {/* Scroll Link Toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsLinkedScrolling(!isLinkedScrolling)}
          className={`flex items-center gap-2 ${
            isLinkedScrolling
              ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          {isLinkedScrolling ? (
            <>
              <Link className="h-4 w-4" />
              Linked Scrolls
            </>
          ) : (
            <>
              <Unlink className="h-4 w-4" />
              Link Scrolls
            </>
          )}
        </Button>
      </div>

      {/* Content Area */}
      <div className="flex gap-6 h-[600px] border rounded-lg">
        {/* Left Side - PDF Preview */}
        <div className="flex-1">
          <div className="border rounded-lg h-full">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-gray-900">Custom Form Preview</h3>
            </div>
            <div
              ref={pdfContainerRef}
              className="h-[calc(100%-3rem)] overflow-auto"
            >
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={pdfUrl}
                  plugins={[zoomPluginInstance]}
                  defaultScale={1.2}
                />
              </Worker>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="w-px bg-gray-300"></div>

        {/* Right Side - Form (controls scrolling) */}
        <div className="flex-1">
          <div className="border rounded-lg h-full">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-gray-900">Output Form</h3>
            </div>
            <div
              ref={formContainerRef}
              className="h-[calc(100%-3rem)] overflow-auto"
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
